import {
  applyOperation,
  normalizeRational,
  isIsolated,
  solve,
  type AlgebraOperation,
  type Equation,
} from './algebra.ts';
import { parseLinearEquationDraft } from './spokenMath.ts';
import { operationNarration } from './tutorGuidance.ts';

/** A tutor example is separate from the learner's equation and never applies a command. */
export interface TutorDemonstration {
  equation: Equation;
  operation: AlgebraOperation;
  after: Equation;
  narration: string;
}

export function nextTutorDemonstration(
  source: string,
): TutorDemonstration | undefined {
  try {
    const equation = parseLinearEquationDraft(source).equation;
    const solution = solve(equation);
    if (solution.kind !== 'unique' || isIsolated(equation, solution.value))
      return;
    const operation: AlgebraOperation | undefined =
      equation.right.x !== '0'
        ? { kind: 'subtract', value: `${equation.right.x}x` }
        : equation.left.constant !== '0'
          ? { kind: 'subtract', value: equation.left.constant }
          : equation.left.x !== '0' && equation.left.x !== '1'
            ? { kind: 'divide', value: equation.left.x }
            : undefined;
    if (!operation) return;
    return {
      equation,
      operation,
      after: applyOperation(equation, operation),
      narration: operationNarration(equation, operation),
    };
  } catch {
    return;
  }
}

export function requestsNextDemonstration(
  question: string,
  plan: TutorDemonstration,
): boolean {
  const text = question.toLowerCase();
  if (!questionMatchesDemonstration(question, plan)) return false;
  if (/\b(?:don't|do not|without|not|zero)\b/.test(text) || /\bby\s+0\b/.test(text)) return false;
  if (
    /\b(?:hint|next step|show (?:me )?how|demonstrate|explain this step|help me solve)\b/.test(
      text,
    )
  )
    return true;
  return plan.operation.kind === 'divide'
    ? /\b(?:divid\w*|sharing|equal groups|fraction)\b/.test(text)
    : /\b(?:subtract\w*|take away|remove the|cancel the)\b/.test(text);
}

export function questionMatchesDemonstration(question: string, plan: TutorDemonstration): boolean {
  const text = question.toLowerCase();
  const requestedKind = /\bdivid\w*\b/.test(text) ? 'divide'
    : /\bsubtract\w*\b/.test(text) ? 'subtract'
    : /\bmultip\w*\b/.test(text) ? 'multiply'
    : /\badd(?:ing)?\b/.test(text) ? 'add' : undefined;
  if (requestedKind && requestedKind !== plan.operation.kind) return false;
  const named = requestedKind === 'divide' || requestedKind === 'multiply'
    ? text.match(/\bby\s+([^\s?!,]+)/)
    : text.match(/\b(?:subtract(?:ing)?|add(?:ing)?)\s+([^\s?!,]+)/);
  if (!named) return true;
  const trailing = text.slice((named.index ?? 0) + named[0].length).trim();
  if (trailing && !/^[?.,!]|^(?:to|from|on|both|tell\w*|give\w*|find\w*|make\w*|help\w*|instead|here|please|first|then)\b/.test(trailing)) return false;
  const words: Record<string, string> = { zero:'0', one:'1', two:'2', three:'3', four:'4', five:'5', six:'6', seven:'7', eight:'8', nine:'9', ten:'10', twelve:'12' };
  const operand = named[1].replace(/\.$/, '');
  try { return normalizeRational(words[operand] ?? operand) === normalizeRational(plan.operation.value); }
  catch { return false; }
}

/** These explanations describe the visible relationship, never an unrendered result. */
export const tutorConcepts = {
  variable: 'A variable stands for a number whose value we may not know yet. Each x in this equation stands for the same number. The letter gives us a way to describe a relationship before we know that number. What does x represent in this problem?',
  equality: 'The equals sign means the expressions on its two sides have the same value. It describes a relationship, rather than telling us to put an answer next. What are the two expressions that the equals sign connects here?',
  coefficient: 'A coefficient is the factor multiplying a variable. In the equation on the board, look at the number immediately before x. It tells us how many copies of the unknown are represented. Can you point to the coefficient?',
  inverse: 'Inverse operations undo one another. To understand a step, ask what operation created the term and what would undo it. A reversible change lets us return to the original relationship. Which operation would you like to see demonstrated?',
  'why-both-sides': 'Both sides describe the same value. Applying the same reversible operation to each entire side preserves that relationship. Changing just one side can break it. Which part of the relationship would you like to examine?',
  clarification: 'I need a little more detail to connect your question to the board. Are you asking about a symbol, why both sides must change together, or a particular operation? Tell me which one and I will help with that.',
} as const;

export interface TutorStoryContext {
  unknownLabel: string;
  rateLabel: string;
  countUnit: string;
  rate: string;
}

export function reviewedTutorConcept(id: keyof typeof tutorConcepts, story?: TutorStoryContext): string {
  if (story && (id === 'variable' || id === 'coefficient')) {
    const variable = `Here, x represents the number of ${story.unknownLabel}. Each x stands for the same unknown number.`;
    const coefficient = `The story gives a ${story.rateLabel} of ${story.rate}. That coefficient tells us how much each unit of the unknown contributes. The total is counted in ${story.countUnit}.`;
    return `${variable} ${coefficient} How would you describe the difference between the unknown and its coefficient?`;
  }
  return tutorConcepts[id];
}

/** Route clear vocabulary questions without a model round trip or classification drift. */
export function requestedTutorConcept(question: string, story?: TutorStoryContext): keyof typeof tutorConcepts | undefined {
  const text = question.toLowerCase();
  if (/\b(?:what (?:does|is) x|what do (?:the )?x(?:s| symbols)?|meaning of x)\b/.test(text) &&
      /\b(?:mean\w*|represent\w*|stand\w*|unknown)\b/.test(text)) return 'variable';
  if (/\b(?:equals? sign|equality symbol)\b/.test(text) &&
      /\b(?:mean\w*|represent\w*|what|why|explain)\b/.test(text)) return 'equality';
  if (/\bcoefficient\b/.test(text) &&
      /\b(?:mean\w*|represent\w*|what|explain)\b/.test(text)) return 'coefficient';
  const numberMeaning = text.match(/\bwhat does (\d+) (?:mean|represent|stand for)\b/);
  if (story && numberMeaning && numberMeaning[1] === story.rate) return 'coefficient';
  return;
}

/** Only the server's available example may be selected; model-supplied algebra is ignored. */
export function decodeTutorResponse(
  raw: string,
  plan?: TutorDemonstration,
  story?: TutorStoryContext,
): {
  text: string;
  demonstration?: TutorDemonstration;
} {
  try {
    const parsed: unknown = JSON.parse(
      raw.replace(/^```(?:json)?\s*|\s*```$/g, ''),
    );
    if (typeof parsed !== 'object' || parsed === null)
      throw new Error('Invalid response');
    const value = parsed as Record<string, unknown>;
    if (value.demonstration === 'next-step' && plan)
      return { text: plan.narration, demonstration: plan };
    if (value.demonstration != null || typeof value.concept !== 'string' ||
      !Object.prototype.hasOwnProperty.call(tutorConcepts, value.concept))
      throw new Error('Invalid response');
    return { text: reviewedTutorConcept(value.concept as keyof typeof tutorConcepts, story) };
  } catch {
    return {
      text: tutorConcepts.clarification,
    };
  }
}
